<script setup lang="ts">
import {computed} from "vue";
import {MessageSquareText} from "@lucide/vue";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from "@/core/components/ui/dialog";
import BaseButton from "@/core/components/base/BaseButton.vue";
import {
    InterventionAddition,
    InterventionDraft,
    InterventionRow,
    InterventionStatus,
    InterventionStatusOption
} from "@/medication-checkup/interfaces/interventions";
import {CHIP, STATUS_CHIPS} from "@/medication-checkup/services/SceneClasses";

/**
 * The last read before the table closes: every treatment of the table and
 * every addition, laid out side by side — the treatment as it stands on the
 * left, what it becomes on the right, row against row so the difference
 * reads from left to right. The ones left as they are stay listed, quiet, so
 * each column reads as a whole regimen. The validation freezes the record,
 * and what it freezes is worth seeing whole.
 *
 * It reads the drafts of the scene, never the record: it stands before the
 * submission, not after it.
 */

const props = defineProps<{
    rows: InterventionRow[];
    drafts: Record<string, InterventionDraft>;
    additions: InterventionAddition[];
    statuses: InterventionStatusOption[];
    busy?: boolean;
}>();

const emit = defineEmits<{ confirm: [] }>();

const open = defineModel<boolean>('open', {required: true});

/**
 * The two columns and the gutter between them, shared by the heading and
 * every row so the cells of a treatment stay level whatever wraps.
 */
const GRID = 'grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-x-3';

/**
 * The cell a change is told in: the tone of its status, faded so the words
 * stay the darkest thing on it.
 */
const CELL = 'flex flex-col gap-0.5 min-w-0 rounded-lg border px-3 py-2';

/**
 * The tone of the side that carries the change. A cell that only restates
 * the treatment stays plain.
 */
const CHANGED: Record<InterventionStatus, string> = {
    unchanged: 'border-slate-200 bg-white',
    posology_modified: 'border-orange bg-orange/10',
    replaced: 'border-lightPurple bg-lightPurple-lightest/60',
    stopped: 'border-red-light/40 bg-red-light/5',
    added: 'border-secondary bg-secondary-lightest/60'
};

const statusLabel = (status: InterventionStatus): string => {
    return props.statuses.find(option => option.key === status)?.label ?? status;
};

const draftOf = (row: InterventionRow): InterventionDraft => {
    return props.drafts[row.key] ?? {status: 'unchanged', product: null, outcome: '', comment: '', analysis: null};
};

/**
 * One side of a treatment: what it is called, and its posology in words.
 */
interface Side {
    label: string;
    posology: string | null;
}

/**
 * One row of the recap, whatever it came from: a row of the table or a
 * treatment to add. A side left null is a treatment that is not there — not
 * yet, for an addition; no longer, for a stop.
 */
interface Change {
    key: string;
    status: InterventionStatus;
    before: Side | null;
    after: Side | null;
    comment: string;
}

/**
 * What the row becomes: the new posology where it carries one, the product
 * that replaces it, nothing at all for a stop.
 */
const afterOf = (row: InterventionRow): Side | null => {
    const draft = draftOf(row);

    switch (draft.status) {
        case 'stopped':
            return null;
        case 'replaced':
            return {label: draft.product?.label ?? '—', posology: null};
        case 'posology_modified':
            return {label: row.label, posology: draft.outcome.trim() || null};
        default:
            return {label: row.label, posology: row.before};
    }
};

/**
 * Every row of the table in its order, the additions last.
 */
const changes = computed<Change[]>(() => {
    const changed = props.rows
        .map(row => ({
            key: row.key,
            status: draftOf(row).status,
            before: {label: row.label, posology: row.before},
            after: afterOf(row),
            comment: draftOf(row).comment.trim()
        }));

    const added = props.additions.map(addition => ({
        key: addition.key,
        status: 'added' as const,
        before: null,
        after: {label: addition.product.label, posology: addition.outcome.trim() || null},
        comment: addition.comment.trim()
    }));

    return [...changed, ...added];
});

/**
 * The rows that say something to the doctor — a treatment left as it is but
 * commented is one of them.
 */
const proposals = computed(() => {
    return changes.value.filter(change => change.status !== 'unchanged' || change.comment !== '').length;
});

const isUnchanged = (change: Change): boolean => change.status === 'unchanged';
</script>

<template>
  <Dialog v-model:open="open">
    <DialogContent class="max-w-[780px] max-h-[90vh] flex flex-col">
      <DialogHeader>
        <DialogTitle class="text-base">
          Valider l'intervention pharmaceutique ?
        </DialogTitle>
        <DialogDescription class="text-[12.5px]">
          {{ proposals }} proposition{{ proposals > 1 ? 's' : '' }} à transmettre au médecin.
          Une fois validé, le tableau ne pourra plus être modifié.
        </DialogDescription>
      </DialogHeader>

      <div class="flex-1 min-h-0 overflow-y-auto -mx-6 px-6 flex flex-col gap-2">
        <div :class="GRID" class="sticky top-0 z-10 bg-white py-1 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
          <span class="px-3">Avant</span>
          <span class="px-3">Après</span>
        </div>

        <div
          v-for="change in changes"
          :key="change.key"
          :class="GRID"
          class="gap-y-1"
        >
          <div
            v-if="change.before"
            :class="CELL"
            class="border-slate-200 bg-white"
          >
            <span
              class="text-xs break-words"
              :class="{
                'font-semibold': !isUnchanged(change),
                'line-through text-muted-foreground': change.status === 'stopped' || change.status === 'replaced'
              }"
            >{{ change.before.label }}</span>
            <span
              class="text-[11.5px] text-muted-foreground break-words"
              :class="{'line-through': change.status === 'posology_modified'}"
            >{{ change.before.posology ?? '—' }}</span>
          </div>
          <div
            v-else
            class="flex items-center justify-center rounded-lg border border-dashed border-gray px-3 py-2 text-[11.5px] text-muted-foreground"
          >
            Pas de traitement
          </div>

          <div
            v-if="change.after"
            :class="[CELL, CHANGED[change.status]]"
          >
            <div class="flex items-start gap-2">
              <span
                class="flex-1 min-w-0 text-xs break-words"
                :class="{'font-semibold': !isUnchanged(change), 'text-primary': change.status === 'replaced'}"
              >{{ change.after.label }}</span>
              <span
                v-if="!isUnchanged(change)"
                class="shrink-0"
                :class="[CHIP, STATUS_CHIPS[change.status]]"
              >{{ statusLabel(change.status) }}</span>
            </div>
            <span
              v-if="change.status !== 'replaced'"
              class="text-[11.5px] break-words"
              :class="change.status === 'posology_modified' || change.status === 'added' ? 'font-semibold' : 'text-muted-foreground'"
            >{{ change.after.posology ?? '—' }}</span>
          </div>
          <div
            v-else
            :class="[CELL, CHANGED[change.status]]"
            class="justify-center"
          >
            <div class="flex items-center gap-2">
              <span class="flex-1 text-xs font-semibold text-red-light">Arrêt</span>
              <span class="shrink-0" :class="[CHIP, STATUS_CHIPS[change.status]]">{{ statusLabel(change.status) }}</span>
            </div>
          </div>

          <span
            v-if="change.comment"
            class="col-span-2 flex items-start gap-1.5 px-3 text-[11px] text-muted-foreground"
          >
            <MessageSquareText class="w-3 h-3 mt-0.5 shrink-0" />
            {{ change.comment }}
          </span>
        </div>
      </div>

      <DialogFooter class="gap-2">
        <BaseButton
          variant="text"
          :disabled="busy"
          @click="open = false"
        >
          Modifier le tableau
        </BaseButton>
        <BaseButton
          primary
          :disabled="busy"
          :loading="busy"
          @click="emit('confirm')"
        >
          Valider l'intervention
        </BaseButton>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
